"use client";

import React from "react";
import { Footer as UIFooter } from "@repo/ui-parkmobile/Footer";
import {
  linkResolver,
  universalLinkResolver,
  imageStoryblokResolver,
} from "@/lib/storyblok/adaptors";
import type { Footer } from "@/generated/sb";
import { storyblokEditable } from "@storyblok/react/rsc";
import { trackFooterLinkClick } from "@/utils/trackingScripts";

interface FooterProps {
  blok: Footer;
}

export function Footer({ blok }: FooterProps) {
  if (!blok) {
    return null;
  }

  const logoImage = blok.logo?.[0];
  const logoAsset = logoImage ? imageStoryblokResolver(logoImage) : undefined;

  const linkGroups = blok.linksGroups?.map((group) => {
    const mainLinkBlok = group.mainLink?.[0];
    const mainLinkProps = mainLinkBlok ? linkResolver([mainLinkBlok]) : null;

    const mainLink = mainLinkBlok
      ? {
          text: mainLinkBlok.text || "",
          href: mainLinkProps?.href ? String(mainLinkProps.href) : "",
          target: mainLinkProps?.target as string | undefined,
        }
      : null;

    const footerCategory = mainLink?.text || "Footer";

    const links = group.links
      ?.map((link) => {
        const linkProps = linkResolver([link]);
        return linkProps && link.text
          ? {
              text: link.text,
              href: String(linkProps.href || ""),
              target: linkProps.target as string | undefined,
              onClick: () => {
                trackFooterLinkClick({
                  footerCategory,
                  footerLinkText: link.text || "",
                  footerLinkURL: String(linkProps.href || ""),
                });
              },
            }
          : null;
      })
      .filter((link): link is NonNullable<typeof link> => link !== null);

    return {
      mainLink,
      links,
    };
  });

  const socialLinks = blok.socialLinks
    ?.map((socialLink) => {
      const linkProps = universalLinkResolver(socialLink.link);
      if (!socialLink.icon?.filename || !linkProps?.href) {
        return null;
      }
      return {
        icon: socialLink.icon.filename,
        alt: socialLink.icon.alt || "",
        href: String(linkProps.href),
        target: linkProps.target as string | undefined,
      };
    })
    .filter((link): link is NonNullable<typeof link> => link !== null);

  const legalLinks = blok.copyrightLinks?.map((link) => {
    const linkProps = linkResolver([link]);
    return {
      text: link.text || "",
      href: String(linkProps?.href || ""),
      target: linkProps?.target as string | undefined,
      onClick: () => {
        trackFooterLinkClick({
          footerCategory: "Legal",
          footerLinkText: link.text || "",
          footerLinkURL: String(linkProps?.href || ""),
        });
      },
    };
  });

  return (
    <div {...storyblokEditable(blok)}>
      <UIFooter
        logo={
          logoAsset
            ? {
                src: logoAsset.src,
                alt: logoAsset.alt,
                width: logoAsset.width,
                height: logoAsset.height,
              }
            : undefined
        }
        languageSelector={{
          currentLanguage: "English",
          onLanguageChange: () => {},
        }}
        linkGroups={linkGroups}
        socialLinks={
          socialLinks && socialLinks.length > 0
            ? {
                title: blok.socialsTitle || blok.socialLinksTitle,
                links: socialLinks,
              }
            : undefined
        }
        legalLinks={legalLinks}
        copyrightText={blok.copyrightText}
      />
    </div>
  );
}
